import { QFlex } from '@qualio/ui-components';
import { Context } from 'ckeditor5';
import { useState } from 'react';
import { useZoomHandler } from '../../../../hooks/ZoomHandler';
import { useDocumentOverviewContext } from '../../DocumentOverview/Context';
import { FileDocumentControls } from '../../FileDocumentPreviewer/FileDocumentControls';
import { FileDocumentLoadingIndicator } from '../../FileDocumentPreviewer/FileDocumentLoadingIndicator';
import { useAttachmentPreview } from '../hooks/useAttachmentPreview';
import {
  handleAttachmentDownload,
  hasXlsxExtension,
  resizeOnZoom,
} from '../utils';
import { FileDocumentLayout } from './FileDocumentLayout';
import { FileDocumentPreviewError } from './FileDocumentPreviewError';

export const FileDocumentPreviewerV2: React.FC<{
  attachmentId: string;
  showDownloadButton: boolean;
  handleSessionDisconnectionEvent?: (didReceive: boolean) => void;
}> = ({
  attachmentId,
  showDownloadButton,
  handleSessionDisconnectionEvent,
}) => {
  const { commentsPermission, collaborationToken, qualioDocument } =
    useDocumentOverviewContext();

  const { attachmentPreview, previewError } =
    useAttachmentPreview(attachmentId);

  const [context] = useState<Context>();
  const [zoom, setZoom] = useState<number>(1);
  const [documentPageCount, setDocumentPageCount] = useState<number>(0);
  const [isPaginated, setIsPaginated] = useState(false);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const handleNavigate = (offset: number) => {
    setPageNumber((prevValue) => prevValue + offset);
  };

  const handlePagination = () => {
    setIsPaginated(!isPaginated);
  };

  const handleZoom = useZoomHandler(setZoom, (zoom) =>
    resizeOnZoom(zoom, context),
  );
  if (previewError) {
    return <FileDocumentPreviewError attachmentId={attachmentId} />;
  }

  if (!attachmentPreview) {
    return <FileDocumentLoadingIndicator />;
  }
  const handleDownload = () =>
    handleAttachmentDownload(
      attachmentId,
      attachmentPreview.mimeType!,
      attachmentPreview.filename!,
    );

  const isXlsx = hasXlsxExtension(attachmentPreview.filename);

  const showComments =
    commentsPermission > 0 &&
    !!collaborationToken &&
    qualioDocument.is_most_recent_version;

  return (
    <QFlex direction={'column'} minWidth={0} overflow="hidden">
      <FileDocumentControls
        handleZoom={handleZoom}
        handleDownload={handleDownload}
        handleNavigate={handleNavigate}
        handlePagination={handlePagination}
        zoom={zoom}
        currentPage={pageNumber}
        isPaginated={isXlsx || isPaginated}
        totalPages={documentPageCount}
        showDownloadButton={showDownloadButton}
        showCommentButton={showComments}
        isXlsx={isXlsx}
      />
      <FileDocumentLayout
        attachmentPreview={attachmentPreview}
        isPaginated={isXlsx || isPaginated}
        pageNumber={pageNumber}
        zoom={zoom}
        isXlsx={isXlsx}
        showComments={showComments}
        onLoadSuccess={setDocumentPageCount}
        handleSessionDisconnectionEvent={handleSessionDisconnectionEvent}
      />
    </QFlex>
  );
};
